import { Bike, Calendar, FileText, Wrench } from 'lucide-react';
import { formatCurrency } from '@/lib/formatters';
import type { DemandaClienteResumo } from './demandaTypes';

type Props = {
  data: DemandaClienteResumo | null;
  loading?: boolean;
};

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('pt-BR');
  } catch {
    return '—';
  }
}

export function DemandaClienteResumoCard({ data, loading }: Props) {
  if (loading) {
    return (
      <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
        Carregando contexto do cliente…
      </div>
    );
  }
  if (!data || !data.available) return null;

  const { veiculo, contrato, financeiro, oficina } = data;
  const motoLabel = [veiculo.marca, veiculo.modelo, veiculo.ano].filter(Boolean).join(' · ') || '—';

  return (
    <div className="rounded-md border bg-muted/20 p-3 space-y-3 text-xs">
      <div className="text-sm font-semibold flex items-center gap-1.5">
        <Bike className="w-4 h-4" />
        Contexto do cliente
      </div>

      <div className="space-y-1">
        <div className="font-medium text-[11px] text-muted-foreground uppercase tracking-wide">
          Veículo / contrato
        </div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
          <div>
            <span className="text-muted-foreground">Placa </span>
            <span className="font-mono font-semibold">{veiculo.placa || '—'}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Contrato </span>
            <span className="font-mono">{contrato.numero || '—'}</span>
          </div>
          <div className="col-span-2">
            <span className="text-muted-foreground">Moto </span>
            {motoLabel}
            {veiculo.cor ? ` · ${veiculo.cor}` : ''}
          </div>
          <div className="flex items-center gap-1">
            <Calendar className="w-3 h-3 text-muted-foreground" />
            Início {fmtDate(contrato.inicio)}
          </div>
          <div>Fim prev. {fmtDate(contrato.fimEsperado)}</div>
          {contrato.progressoPct != null ? (
            <div className="col-span-2">
              <div className="flex justify-between mb-0.5">
                <span className="text-muted-foreground">Progresso do contrato</span>
                <span className="font-semibold">{contrato.progressoPct}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full"
                  style={{ width: `${contrato.progressoPct}%` }}
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="space-y-1">
        <div className="font-medium text-[11px] text-muted-foreground uppercase tracking-wide flex items-center gap-1">
          <FileText className="w-3 h-3" />
          Financeiro
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="rounded bg-background/80 border px-2 py-1.5 text-center">
            <div className="text-[10px] text-muted-foreground">Abertos</div>
            <div className="font-semibold text-sm">{financeiro.titulosAbertos}</div>
          </div>
          <div className="rounded bg-background/80 border px-2 py-1.5 text-center">
            <div className="text-[10px] text-muted-foreground">Vencidos</div>
            <div className="font-semibold text-sm text-amber-700">{financeiro.titulosVencidos}</div>
          </div>
          <div className="rounded bg-background/80 border px-2 py-1.5 text-center">
            <div className="text-[10px] text-muted-foreground">Pagos</div>
            <div className="font-semibold text-sm text-emerald-700">{financeiro.titulosPagos}</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 pt-0.5">
          <span>
            Saldo em aberto:{' '}
            <strong>{formatCurrency(financeiro.saldoAberto)}</strong>
          </span>
          <span>
            Valor pago:{' '}
            <strong>{formatCurrency(financeiro.valorPago)}</strong>
          </span>
        </div>
      </div>

      <div className="space-y-0.5">
        <div className="font-medium text-[11px] text-muted-foreground uppercase tracking-wide flex items-center gap-1">
          <Wrench className="w-3 h-3" />
          Oficina
        </div>
        <div>
          {oficina.quantidadeOs} O.S. · Última: {fmtDate(oficina.ultimaOsEm)}
        </div>
      </div>
    </div>
  );
}
